import type React from "react"
import type { LucideIcon } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Building2, User, Network } from "lucide-react"
import { useLanguage } from "@/contexts/language-context"
import { useClientProfiles } from "@/hooks/use-client-profiles"
import { useOrganizations } from "@/hooks/use-organizations"
import { useBranches } from "@/hooks/use-branches"
import { useCodeValues } from "@/hooks/use-code-values"
import { CODE_TABLE_CATEGORIES } from "@/lib/constants/code-table-categories"
import { ENGAGEMENT_TYPE } from "@/lib/constants/engagement-codes"
import type { WorkerProfileSummary, OrganizationSummary } from "@/types/api/engagement"

interface EngagementHeaderProps {
  selectedClient: number
  selectedContact: number
  selectedBranch: string
  engagementType: string
  /** Server-resolved org names — fallback for the client name when the RLS-gated list is empty. */
  organizations?: Record<number, OrganizationSummary>
  /** Server-resolved profile names — fallback for the client contact when the RLS-gated list is empty. */
  profileSummaries?: Record<number, WorkerProfileSummary>
}

function HeaderInfoItem({ 
  icon: Icon, 
  label, 
  value,
  className = ""
}: { 
  icon: LucideIcon
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <Icon className="h-4 w-4 text-primary/70" strokeWidth={2.5} />
      <span className="text-base text-muted-foreground">{label}</span>
      <span className="text-base font-medium ml-2">{value}</span>
    </div>
  )
}

export function EngagementHeader({
  selectedClient,
  selectedContact,
  selectedBranch,
  engagementType,
  organizations,
  profileSummaries
}: EngagementHeaderProps) {
  const { dictionary } = useLanguage()
  
  const {
    clientOrganizations,
  } = useOrganizations()
  // Get active branches (include current branch even if inactive for viewing)
  const { branches, loading: loadingBranches, error: branchesError } = useBranches({
    includeInactiveBranchCode: selectedBranch || undefined
  })
  const { getCodeValueByCode } = useCodeValues()

  // Client profiles hook - loads client contacts for the selected client
  const { 
    clientProfiles: clientContacts,
  } = useClientProfiles({ organizationId: selectedClient })

  // Determine if this is an internal engagement
  const isInternal = engagementType === ENGAGEMENT_TYPE.INTERNAL

  return (
    <Card>
      <CardContent className="p-4">
        {/* GW-330: status and pre-engagement pills live in EngagementToolbar for every mode, so this
            card carries the engagement's parties only.
            Client, Contact, Branch (hide Client/Contact for Internal engagements) */}
        <div className="flex items-center divide-x divide-border">
          {!isInternal && (
            <>
              <HeaderInfoItem
                icon={Building2}
                label={dictionary?.engagement?.client || "Client"}
                value={clientOrganizations.find(org => org.Id === selectedClient)?.DisplayName || organizations?.[selectedClient]?.displayName || (selectedClient > 0 ? `Organization ID ${selectedClient}` : "-")}
                className="pr-6"
              />
              <HeaderInfoItem
                icon={User}
                label={dictionary?.engagement?.contact || "Contact"}
                value={clientContacts.find(c => c.Id === selectedContact)?.FullName || profileSummaries?.[selectedContact]?.fullName || (selectedContact > 0 ? `Profile ID ${selectedContact}` : "-")}
                className="px-6"
              />
            </>
          )}
          <HeaderInfoItem
            icon={Network}
            label={dictionary?.engagement?.branch || "Branch"}
            value={loadingBranches ? "Loading..." : branchesError ? "Error loading" : branches.find(b => b.Code === selectedBranch)?.Name || (selectedBranch ? (getCodeValueByCode(selectedBranch, CODE_TABLE_CATEGORIES.BRANCH)?.text ?? selectedBranch) : "-")}
            className={isInternal ? 'pr-6' : 'px-6'}
          />
        </div>
      </CardContent>
    </Card>
  )
} 